import { useEffect, useRef } from "react";
import { Button } from "flowbite-react";
import { ArrowRight } from "lucide-react";
import { PREFERENCE_DEFINITIONS, READY_COPY } from "@/features/legal/config/onboarding.config";
import type { OnboardingPreferences } from "@/features/legal/types/onboarding.type";
import { cn } from "@/shared/utils/style.util";

type ReadyStepProps = {
  versionLabel: string
  preferences: OnboardingPreferences
  onEnter: () => void
  onEditPreferences: () => void
};

const SUMMARY_BADGE_CLASS = "rounded-full px-3 py-1 text-xs font-medium";

type SummaryRowProps = {
  label: string
  value: string
  isOn: boolean
};

const SummaryRow = ({ label, value, isOn }: SummaryRowProps) => (
  <div className="flex items-center justify-between gap-4 py-3">
    <dt className="text-sm text-gray-600 dark:text-gray-300">{label}</dt>
    <dd
      className={cn(
        SUMMARY_BADGE_CLASS,
        isOn ? "bg-success/15 text-emerald-800 dark:text-emerald-300" : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
      )}
    >
      {value}
    </dd>
  </div>
);

export const ReadyStep = ({
  versionLabel, preferences, onEnter, onEditPreferences,
}: ReadyStepProps) => {
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true });
  }, []);

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <h1 ref={headingRef} tabIndex={-1} className="text-3xl font-bold leading-tight tracking-tight text-gray-900 outline-none dark:text-white">
          {READY_COPY.TITLE}
        </h1>
        <p className="text-base leading-7 text-gray-600 dark:text-gray-300">{READY_COPY.INTRO}</p>
      </div>
      <dl className="divide-y divide-gray-100 rounded-2xl border border-gray-200 px-4 dark:divide-gray-800 dark:border-gray-700" data-testid="onboarding-summary">
        <SummaryRow label={READY_COPY.CGU_SUMMARY_LABEL(versionLabel)} value={READY_COPY.CGU_ACCEPTED} isOn />
        {PREFERENCE_DEFINITIONS.map((definition) => {
          const isOn = preferences[definition.field];
          return (
            <SummaryRow
              key={definition.field}
              label={definition.summaryLabel}
              value={isOn ? definition.summaryOn : definition.summaryOff}
              isOn={isOn}
            />
          );
        })}
      </dl>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <button
          type="button"
          onClick={onEditPreferences}
          className="text-sm text-gray-500 hover:text-primary dark:text-gray-400"
          data-testid="onboarding-edit-preferences-button"
        >
          {READY_COPY.EDIT_PREFERENCES}
        </button>
        <Button pill color="primary" onClick={onEnter} data-testid="onboarding-enter-button">
          <span className="flex items-center gap-2">
            {READY_COPY.ENTER}
            <ArrowRight size={16} aria-hidden />
          </span>
        </Button>
      </div>
    </div>
  );
};

ReadyStep.displayName = "ReadyStep";
